import { useRef, useState, useCallback, useEffect } from 'react';
import TurnstileField from '../TurnstileField';
import { submitContactForm } from '../../lib/submitContactForm';
import { validateContactForm, MESSAGE_MAX_LENGTH } from '../../lib/validateContactForm';
import { isLocalDev } from '../../lib/isLocalDev';

const INITIAL = { email: '', subject: '', message: '' };

const ContactForm = ({ isOpen, onSuccess }) => {
  const [fields, setFields] = useState(INITIAL);
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileRequired, setTurnstileRequired] = useState(!isLocalDev());
  const turnstileRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setFields(INITIAL);
    setFieldErrors({});
    setStatus('idle');
    setError('');
    setTurnstileToken('');
    turnstileRef.current?.reset();
  }, [isOpen]);

  const handleTurnstileReady = useCallback((isAvailable) => {
    setTurnstileRequired(isAvailable);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'message' ? value.slice(0, MESSAGE_MAX_LENGTH) : value;
    setFields((prev) => ({ ...prev, [name]: nextValue }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (status === 'error') {
      setStatus('idle');
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const honey = e.currentTarget.elements._honey?.value;
    if (honey) return;

    const validation = validateContactForm(fields);
    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setStatus('error');
      setError('Please complete all required fields before sending.');
      return;
    }

    if (turnstileRequired && !turnstileToken) {
      setStatus('error');
      setError('Please complete the security check before sending.');
      return;
    }

    if (isLocalDev()) {
      setStatus('error');
      setError('Contact form can only be sent on the live site. Open andreafagioli.com to test.');
      return;
    }

    setFieldErrors({});
    setStatus('sending');
    setError('');

    try {
      await submitContactForm({
        ...validation.values,
        turnstileToken,
        honey,
      });
      setFields(INITIAL);
      setTurnstileToken('');
      turnstileRef.current?.reset();
      setStatus('success');
      window.setTimeout(() => {
        onSuccess?.();
      }, 1800);
    } catch (err) {
      turnstileRef.current?.reset();
      setTurnstileToken('');
      setStatus('error');
      setError(err.message || 'Unable to send your message. Please try again.');
    }
  };

  const isSending = status === 'sending';

  return (
    <form
      className="cta-form"
      onSubmit={handleSubmit}
      noValidate
      data-testid="contact-form"
    >
      <label className="cta-form-honey" aria-hidden="true">
        <span>Leave empty</span>
        <input type="text" name="_honey" tabIndex={-1} autoComplete="off" />
      </label>

      <div className="cta-form-grid">
        <div className="cta-form-field">
          <label htmlFor="contact-email" className="cta-form-label">
            — Your email <span className="cta-form-required" aria-hidden="true">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            className={`cta-form-input${fieldErrors.email ? ' is-invalid' : ''}`}
            value={fields.email}
            onChange={handleChange}
            required
            autoComplete="email"
            disabled={isSending}
            aria-invalid={Boolean(fieldErrors.email)}
            aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
            data-testid="contact-email"
          />
          {fieldErrors.email && (
            <p id="contact-email-error" className="cta-form-field-error" role="alert">
              {fieldErrors.email}
            </p>
          )}
        </div>

        <div className="cta-form-field">
          <label htmlFor="contact-subject" className="cta-form-label">
            — Subject <span className="cta-form-required" aria-hidden="true">*</span>
          </label>
          <input
            id="contact-subject"
            name="subject"
            type="text"
            className={`cta-form-input${fieldErrors.subject ? ' is-invalid' : ''}`}
            value={fields.subject}
            onChange={handleChange}
            required
            minLength={2}
            disabled={isSending}
            aria-invalid={Boolean(fieldErrors.subject)}
            aria-describedby={fieldErrors.subject ? 'contact-subject-error' : undefined}
            data-testid="contact-subject"
          />
          {fieldErrors.subject && (
            <p id="contact-subject-error" className="cta-form-field-error" role="alert">
              {fieldErrors.subject}
            </p>
          )}
        </div>

        <div className="cta-form-field cta-form-field-full">
          <label htmlFor="contact-message" className="cta-form-label">
            — Message <span className="cta-form-required" aria-hidden="true">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            className={`cta-form-textarea${fieldErrors.message ? ' is-invalid' : ''}`}
            value={fields.message}
            onChange={handleChange}
            required
            minLength={10}
            maxLength={MESSAGE_MAX_LENGTH}
            rows={5}
            disabled={isSending}
            aria-invalid={Boolean(fieldErrors.message)}
            aria-describedby="contact-message-meta"
            data-testid="contact-message"
          />
          <p
            id="contact-message-meta"
            className={`cta-form-char-count${fields.message.length >= MESSAGE_MAX_LENGTH ? ' is-limit' : ''}`}
          >
            {fields.message.length} / {MESSAGE_MAX_LENGTH}
          </p>
          {fieldErrors.message && (
            <p id="contact-message-error" className="cta-form-field-error" role="alert">
              {fieldErrors.message}
            </p>
          )}
        </div>
      </div>

      <TurnstileField
        ref={turnstileRef}
        onToken={setTurnstileToken}
        onExpire={() => setTurnstileToken('')}
        onReady={handleTurnstileReady}
      />

      {isLocalDev() && (
        <p className="cta-form-dev-note">
          Security check disabled in local dev — send messages on the live site.
        </p>
      )}

      <div className="cta-form-actions">
        <button
          type="submit"
          className="cta-form-submit"
          disabled={isSending || (turnstileRequired && !turnstileToken)}
          data-testid="contact-submit"
        >
          <span className="cta-form-submit-text">
            {isSending ? 'Sending…' : 'Send message'}
          </span>
          <span className="cta-form-submit-arrow" aria-hidden="true">↗</span>
        </button>

        <div className="cta-form-feedback" role="status" aria-live="polite">
          {status === 'success' && (
            <p className="cta-form-status cta-form-status-success">
              Message sent — I&apos;ll get back to you soon.
            </p>
          )}
          {status === 'error' && error && (
            <p className="cta-form-status cta-form-status-error">{error}</p>
          )}
        </div>
      </div>
    </form>
  );
};

export default ContactForm;
